import { RequirementWorkflowType } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { DocumentDetailsTen99PolicyCoveragePage } from "@src/appV2/redesign/Documents/Details/Workflows/Ten99PolicyCoverage/Page";
import { Route, Switch, useLocation, useParams, useRouteMatch } from "react-router-dom";

import { ListLoadingState } from "../../components/ListLoadingState";
import { DocumentsErrorPage } from "../ErrorPage";
import { DOCUMENT_PREVIEW_PATH } from "../paths";
import { type DocumentDetailsPageParams } from "../types";
import { DocumentDetailsModalRoutes } from "./DocumentDetailsModalRoutes";
import { DocumentDetailsProfessionalReferencesPage } from "./ProfessionalReferences/Page";
import { DocumentDetailsUploadPreviewPage } from "./Upload/PreviewPage";
import { useGetDocumentDetailsPageRequirement } from "./useGetDocumentDetailsPageRequirement";
import { RequirementDetailsProvider } from "./useRequirementDetailsContext";
import { DocumentDetailsBlsCprCertificationPage } from "./Workflows/BlsCprCertification/Page";
import { DocumentDetailsCheckrPage } from "./Workflows/Checkr/Page";
import { DocumentDetailsCompositePage } from "./Workflows/Composite/Page";
import { DocumentDetailsUploadPage } from "./Workflows/DocumentUpload/Page";
import { DocumentDetailsSignWithHelloSignPage } from "./Workflows/HelloSign/Page";
import { MiscellaneousDocumentPage } from "./Workflows/Misc/Page";
import { DocumentDetailsPersonalIdStripeVerificationPage } from "./Workflows/Stripe/Page";
import { DocumentDetailsTbTestWithAppointmentPage } from "./Workflows/TbTestWithAppointment/Page";

export function DocumentDetailsPageContainer() {
  const { requirementId } = useParams<DocumentDetailsPageParams>();
  const match = useRouteMatch();

  const { requirement, isLoading, isError } = useGetDocumentDetailsPageRequirement({
    requirementId,
  });
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const shiftId = searchParams.get("shiftId") ?? undefined;

  useLogEffect(
    APP_V2_USER_EVENTS.DOCUMENT_DETAILS_SCREEN_VIEWED,
    {
      requirement_id: requirement?.requirementId,
    },
    { enabled: isDefined(requirement?.requirementId) }
  );

  if (isLoading) {
    return <ListLoadingState texts={["Loading documents..."]} />;
  }

  if (isError || !isDefined(requirement)) {
    logEvent(APP_V2_APP_EVENTS.DOCUMENT_DETAILS_PAGE_NOT_FOUND, {
      requirementId,
    });
    return <DocumentsErrorPage title="Error loading document" />;
  }

  const workflowType = requirement.requirement?.attributes.requirementWorkflowType;

  function getRequirementWorkflowComponent() {
    switch (workflowType) {
      case RequirementWorkflowType.DOCUMENT_UPLOAD: {
        return <DocumentDetailsUploadPage />;
      }

      case RequirementWorkflowType.CHECKR_BACKGROUND_CHECK: {
        return <DocumentDetailsCheckrPage />;
      }

      case RequirementWorkflowType.COMPOSITE: {
        return <DocumentDetailsCompositePage />;
      }

      case RequirementWorkflowType.SIGN_WITH_HELLOSIGN: {
        return <DocumentDetailsSignWithHelloSignPage />;
      }

      case RequirementWorkflowType.MISCELLANEOUS: {
        return <MiscellaneousDocumentPage />;
      }

      case RequirementWorkflowType.PERSONAL_ID_STRIPE_VERIFICATION: {
        return <DocumentDetailsPersonalIdStripeVerificationPage />;
      }

      case RequirementWorkflowType.PROFESSIONAL_REFERENCES: {
        return <DocumentDetailsProfessionalReferencesPage />;
      }

      case RequirementWorkflowType.BLS_CPR_CERTIFICATION_QUIZ: {
        return <DocumentDetailsBlsCprCertificationPage />;
      }

      case RequirementWorkflowType.TB_TEST_WITH_APPOINTMENT: {
        return <DocumentDetailsTbTestWithAppointmentPage />;
      }

      case RequirementWorkflowType.TEN99_POLICY_COVERAGE: {
        return <DocumentDetailsTen99PolicyCoveragePage shiftId={shiftId} />;
      }

      default: {
        return <DocumentDetailsUploadPage />;
      }
    }
  }

  return (
    <RequirementDetailsProvider requirement={requirement}>
      <Switch>
        <Route path={`${match.url}/${DOCUMENT_PREVIEW_PATH}`}>
          <DocumentDetailsUploadPreviewPage />
          <DocumentDetailsModalRoutes basePath={`${match.url}/${DOCUMENT_PREVIEW_PATH}`} />
        </Route>

        <Route path={match.url}>
          {getRequirementWorkflowComponent()}
          <DocumentDetailsModalRoutes basePath={match.url} />
        </Route>
      </Switch>
    </RequirementDetailsProvider>
  );
}
